import { useTracker } from "@ferrivia/auth-web";
import { useTranslation } from "@ferrivia/i18n";
import { Button, Card, Text } from "@ferrivia/ui";
import type { CoachingNextStepBlock as CoachingNextStepBlockData, JourneyStepId } from "@ferrivia/api-contract";
import { useTabNavigation } from "../../../../app/useTabNavigation";
import s from "./CoachingNextStepBlock.module.css";

const STEP_ORDER: JourneyStepId[] = ["labs", "screener", "booking"];

/**
 * The US pilot's welcome card: what the programme is, then the one coaching
 * step the server says is next. The steps themselves live on the Coaching
 * tab, so the CTA goes there rather than into a step.
 */
export function CoachingNextStepBlock({ id, step }: CoachingNextStepBlockData) {
  const { t } = useTranslation("client");
  const { goToTab } = useTabNavigation();
  const tracker = useTracker();

  // Static lookups: i18next-parser only extracts literal-key t() calls.
  const next: Record<JourneyStepId, { title: string; body: string }> = {
    labs: {
      title: t("home.blocks.coachingNextStep.labs.title", "Lisää laboratoriotuloksesi"),
      body: t(
        "home.blocks.coachingNextStep.labs.body",
        "Puhelua varten tarvitaan enintään kuukauden vanhat laboratoriotulokset. Lataa raportti tai syötä arvot itse.",
      ),
    },
    screener: {
      title: t("home.blocks.coachingNextStep.screener.title", "Täytä terveyskysely"),
      body: t(
        "home.blocks.coachingNextStep.screener.body",
        "Vastauksesi auttavat Maria valmistautumaan puheluunne. Voit tallentaa ja jatkaa myöhemmin.",
      ),
    },
    booking: {
      title: t("home.blocks.coachingNextStep.booking.title", "Varaa puhelu Marin kanssa"),
      body: t(
        "home.blocks.coachingNextStep.booking.body",
        "Tuloksesi ja vastauksesi ovat perillä. Valitse sinulle sopiva aika Marin kalenterista.",
      ),
    },
  };

  const handleClick = () => {
    tracker.track("home.block_cta_clicked", { block_id: id });
    goToTab("/support", "in_app_link");
  };

  return (
    <Card role="region" className={s.root} aria-labelledby="coaching-next-step-heading">
      <Text as="h2" size="lg" weight="bold" id="coaching-next-step-heading" className={s.heading}>
        {t("home.blocks.coachingNextStep.heading", "Seuraava vaiheesi")}
      </Text>
      <Text as="p" size="sm" className={s.intro}>
        {t("home.blocks.coachingNextStep.intro", "Kiitos, että liityit Ferrivian pilottiin – olemme iloisia, että olet mukana! Seuraa alla olevia ohjeita.")}
      </Text>
      <div className={s.next}>
        <Text as="p" size="sm" weight="medium" className={s.stepCount}>
          {t("home.blocks.coachingNextStep.stepCount", "Vaihe {{n}}/{{total}}", {
            n: STEP_ORDER.indexOf(step) + 1,
            total: STEP_ORDER.length,
          })}
        </Text>
        <Text as="h3" size="md" weight="bold" className={s.stepTitle}>
          {next[step].title}
        </Text>
        <Text as="p" size="sm" className={s.stepBody}>
          {next[step].body}
        </Text>
      </div>
      <Button className={s.cta} onClick={handleClick}>
        {t("home.blocks.coachingNextStep.cta", "Siirry valmennukseen")}
      </Button>
    </Card>
  );
}
